import QueryTags from '../../types/QueryTags'
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import {
  FetchBillercategoriesResponse,
  FetchBillerIconResponse,
  FetchBillerFieldsResponse,
  ValidateBillIdRequest,
  ValidateBillIdResponse,
  InitiateBillPaymentRequest,
  InitiateBillPaymentResponse,
  PaymentHistoryResponse,
  AddFavourateBillerRequest,
  AddFavourateBillerResponse,
  FavouriteBillersResponse,
  SchedulePaymentsRequest,
  FetchScheduledPaymentsResponse,
  ValidateBillerLimitRequest,
  ValidateBillerLimitResponse,
  DeleteScheduledPaymentsResponse,
  PaymentHistoryRequest,
  FetchBeneficiariesPerBillerRequest,
  FetchBeneficiariesPerBillerResponse,
  fetchBeneficiaryReferencePerBillerRequest,
  FetchBeneficiaryReferencePerBillerResponse,
} from './types'
import { ValidateBalanceResponse } from '../transfers/types/response'
import { getBalanceValidationParams } from 'utils/balanceValidation'

const paymentsBaseUrl = 'biller-service'
const imagesBaseUrl = '/bkimageservice/fileManager'

const billPaymentsEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    QueryTags,
    'data'
  >
) => ({
  getBillerCategories: builder.query<FetchBillercategoriesResponse, string>({
    query: (customerId) => `${paymentsBaseUrl}/customer/${customerId}/biller`,
  }),
  getIcon: builder.query<FetchBillerIconResponse, string>({
    query: (iconId) => `${imagesBaseUrl}/${iconId}`,
  }),
  getFavouriteBillers: builder.query<FavouriteBillersResponse, string>({
    query: (customerId) =>
      `${paymentsBaseUrl}/customer/${customerId}/biller/is-favourite/true`,
    providesTags: ['favouriteBillers'],
  }),
  addFavourateBiller: builder.mutation<
    AddFavourateBillerResponse,
    AddFavourateBillerRequest
  >({
    query: (body) => {
      const { favourateBillers, customerId } = body
      return {
        url: `${paymentsBaseUrl}/mark-favourite-biller/${customerId}`,
        method: 'PATCH',
        body: favourateBillers,
      }
    },
    invalidatesTags: ['favouriteBillers'],
  }),
  getBillerFields: builder.query<FetchBillerFieldsResponse, string>({
    query: (billerId) => `${paymentsBaseUrl}/view-biller/${billerId}/field`,
  }),

  validateBillId: builder.mutation<
    ValidateBillIdResponse,
    ValidateBillIdRequest
  >({
    query: (body) => {
      return {
        url: `${paymentsBaseUrl}/validate-biller`,
        method: 'POST',
        body,
      }
    },
  }),
  validateBillerLimit: builder.mutation<
    ValidateBillerLimitResponse,
    ValidateBillerLimitRequest
  >({
    query: (body) => ({
      url: `${paymentsBaseUrl}/validate-transaction-limit`,
      params: body,
      method: 'GET',
    }),
  }),

  makePayment: builder.mutation<
    InitiateBillPaymentResponse,
    InitiateBillPaymentRequest
  >({
    query: (body) => {
      const { payload, userChannel, otp, otpIdentifier } = body
      return {
        url: `${paymentsBaseUrl}/payment-initiation?emailOrPhoneNumber=${userChannel}&otpCode=${otp}&otpIdentifier=${otpIdentifier}`,
        method: 'POST',
        body: payload,
      }
    },
    invalidatesTags: ['paymentHistory'],
  }),

  getPaymentHistory: builder.query<
    PaymentHistoryResponse,
    PaymentHistoryRequest
  >({
    query: (payload) => {
      return {
        url: `${paymentsBaseUrl}/view-customer-transaction/${payload?.customerId}`,
        method: 'GET',
        params: {
          accountNumber: payload?.accountNumber,
          limit: 10,
          page: payload?.page,
          billType: payload?.billType,
        },
      }
    },
    providesTags: ['paymentHistory']
  }),

  schedulePayment: builder.mutation<
    InitiateBillPaymentResponse,
    SchedulePaymentsRequest
  >({
    query: (body) => {
      return {
        url: `${paymentsBaseUrl}/initiate-schedule-payment`,
        method: 'POST',
        body,
      }
    },
  }),
  getScheduledPayments: builder.query<FetchScheduledPaymentsResponse, string>({
    query: (customerId) =>
      `${paymentsBaseUrl}/customer/${customerId}/schedule-payment`,
    providesTags: ['deleteSchedulepayment'],
  }),

  deleteSchedulePayment: builder.mutation<
    DeleteScheduledPaymentsResponse,
    string
  >({
    query: (id) => ({
      url: `${paymentsBaseUrl}/scheduled-payment/${id}`,
      method: 'DELETE',
    }),
    invalidatesTags: ['deleteSchedulepayment'],
  }),
  fetchBeneficiariesPerBiller: builder.query<FetchBeneficiariesPerBillerResponse,FetchBeneficiariesPerBillerRequest>({
    query: ({customerId, billType}) => `/beneficiary-service/beneficiary-type/single/bill/${customerId}/${billType}`
  }),
  fetchBeneficiaryReferencesPerBiller: builder.query<FetchBeneficiaryReferencePerBillerResponse, fetchBeneficiaryReferencePerBillerRequest>({
    query: ({customerId, beneficiaryId, billType}) => `/beneficiary-service/beneficiary-type/single/bill/${customerId}/${beneficiaryId}/${billType}`
  }),

  requestBillPaymentProducts: builder.mutation<any, any>({
    query: ({body, url, params, method})  => {
      return {
        url: `${paymentsBaseUrl}${url}`,
        method: method,
        body:body,
        params: params
      }
    }
  }),
  retryFailedNotification: builder.mutation<any, any>({
    query: (payload) => {
      return {
        url: `${paymentsBaseUrl}/retry-notification/${payload?.transactionId}`,
        method: 'PUT',
      }
    },
    invalidatesTags: ['paymentHistory'],
  }),

  checkRecentPayment: builder.query<any, {profileId: string, billType:string,}>({
    query: ({profileId, billType}) => {
      return {
        url: `${paymentsBaseUrl}/view-customer-transaction/${profileId}?page=0&limit=5&status=COMPLETED&type=${billType}`,
        method: 'GET',
      }
    }
  }),

  validateBillerBalance: builder.query<ValidateBalanceResponse, { accountNumber: string, amount: string, transactionCurrency: string, debitCurrency: string, transactionType: string, creditAccount: string, creditCurrency: string, hasBkAdvance?: boolean, }>({
    query: (args) => {
      return ({
        url:`${paymentsBaseUrl}/validate-account-balance`,
        method: 'GET',
        params: { ...args, ...getBalanceValidationParams() },
      })
    }
  }),
})

export default billPaymentsEndpoints 
